import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Alert } from '../components/common/Alert';

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  password?: string;
  roleId: string;
  departmentId?: string;
  teamId?: string;
  managerId?: string;
  phoneNumber?: string;
  jobTitle?: string;
  initials?: string;
  isActive: boolean;
  invoiceAccess: boolean;
  invoiceOnlyAccess: boolean;
}

export const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>({
    defaultValues: {
      isActive: true,
      invoiceAccess: false,
      invoiceOnlyAccess: false
    }
  });

  // Fetch user if editing
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      if (!id) return null;
      console.log('Fetching user data for ID:', id);
      const response = await api.get(`/users/${id}`);
      console.log('User API response:', response.data);
      return response.data;
    },
    enabled: isEditing
  });

  // Fetch lookup data
  const { data: roles, isError: rolesError } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      try {
        console.log('Fetching roles...');
        const response = await api.get('/roles');
        console.log('Roles response:', response.data);
        const roles = response.data.roles || response.data || [];
        console.log('Processed roles:', roles);
        return roles;
      } catch (error) {
        console.error('Roles loading error:', error);
        return [];
      }
    },
    retry: 1
  });

  const { data: departments, isError: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        const response = await api.get('/departments');
        console.log('Departments response:', response.data);
        return response.data.departments || response.data || [];
      } catch (error) {
        console.error('Departments loading error:', error);
        return [];
      }
    },
    retry: 1
  });

  const { data: teams, isError: teamsError } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      try {
        const response = await api.get('/teams');
        console.log('Teams response:', response.data);
        return response.data.teams || response.data || [];
      } catch (error) {
        console.error('Teams loading error:', error);
        return [];
      }
    },
    retry: 1
  });

  const { data: managers, isError: managersError } = useQuery({
    queryKey: ['managers'],
    queryFn: async () => {
      try {
        const response = await api.get('/users/for-manager-selection');
        console.log('Users response for managers:', response.data);
        const allUsers = response.data.users || response.data || [];
        // No need to filter current user as endpoint already does it
        return allUsers;
      } catch (error) {
        console.error('Managers loading error:', error);
        return [];
      }
    },
    retry: 1
  });

  // Reset form when user data is loaded
  useEffect(() => {
    if (user && roles && !userLoading) {
      console.log('Loading user data:', user);
      console.log('User roles:', user.roles);
      console.log('Available roles in dropdown:', roles);
      console.log('User departments:', user.departments); 
      console.log('User teams:', user.teams);
      console.log('User initials:', user.initials);
      
      const formData = {
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        employeeId: user.employee_id || '',
        roleId: user.roles?.[0]?.id?.toString() || '',
        departmentId: user.departments?.[0]?.id?.toString() || '',
        teamId: user.teams?.[0]?.id?.toString() || '',
        managerId: user.managers?.[0]?.id?.toString() || '',
        phoneNumber: user.phone,
        jobTitle: user.position,
        initials: user.initials || '',
        isActive: user.is_active,
        invoiceAccess: user.invoice_access || false,
        invoiceOnlyAccess: user.invoice_only_access || false
      };
      
      console.log('Form data to be set:', formData);
      reset(formData);
      console.log('Form reset completed');
    } else {
      console.log('Form reset skipped - waiting for data:', {
        hasUser: !!user,
        hasRoles: !!roles,
        isUserLoading: userLoading
      });
    }
  }, [user, roles, userLoading, reset]);

  const createUser = useMutation({
    mutationFn: async (data: UserFormData) => {
      console.log('Creating user with data:', data);
      console.log('Initials value:', data.initials);
      
      try {
        const response = await api.post('/users', {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          employee_id: data.employeeId,
          password: data.password,
          phone: data.phoneNumber,
          position: data.jobTitle,
          initials: data.initials,
          is_active: data.isActive,
          invoice_access: data.invoiceAccess,
          invoice_only_access: data.invoiceOnlyAccess,
          role_ids: data.roleId ? [parseInt(data.roleId)] : []
        });
        
        console.log('User created successfully:', response.data);
        const userId = response.data.id || response.data.user?.id;
        
        if (!userId) {
          throw new Error('User ID not returned from creation');
        }
        
        // Handle department assignment if selected
        if (data.departmentId) {
          try {
            await api.post(`/users/${userId}/departments`, {
              department_id: parseInt(data.departmentId)
            });
            console.log('Department assigned successfully');
          } catch (error) {
            console.warn('Department assignment failed:', error);
            // Don't fail the whole operation
          }
        }
        
        // Handle team assignment if selected
        if (data.teamId) {
          try {
            await api.post(`/users/${userId}/teams`, {
              team_id: parseInt(data.teamId)
            });
            console.log('Team assigned successfully');
          } catch (error) {
            console.warn('Team assignment failed:', error);
            // Don't fail the whole operation
          }
        }
        
        // Handle manager assignment if selected
        if (data.managerId) {
          try {
            await api.post(`/users/${userId}/managers`, {
              manager_id: parseInt(data.managerId)
            });
            console.log('Manager assigned successfully');
          } catch (error) {
            console.warn('Manager assignment failed:', error);
            // Don't fail the whole operation
          }
        }
        
        return response.data;
      } catch (error) {
        console.error('User creation failed:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('User creation completed, navigating to users list');
      navigate('/users');
    },
    onError: (error: any) => {
      console.error('User creation error:', error);
    }
  });

  const updateUser = useMutation({
    mutationFn: async (data: UserFormData) => {
      console.log('Updating user with data:', data);
      console.log('Initials value:', data.initials);
      
      // Update basic user info
      const updatePayload = {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        employee_id: data.employeeId,
        phone: data.phoneNumber,
        position: data.jobTitle,
        initials: data.initials,
        is_active: data.isActive,
        invoice_access: data.invoiceAccess,
        invoice_only_access: data.invoiceOnlyAccess
      };
      
      console.log('Sending PATCH request to /users/' + id);
      console.log('Update payload:', updatePayload);
      console.log('JSON stringify payload:', JSON.stringify(updatePayload, null, 2));
      
      const response = await api.patch(`/users/${id}`, updatePayload);
      console.log('Update response:', response.data);
      
      // Handle role change if needed
      if (data.roleId && user?.roles?.[0]?.id !== parseInt(data.roleId)) {
        // Remove old role if exists
        if (user?.roles?.[0]?.id) {
          await api.delete(`/users/${id}/roles/${user.roles[0].id}`);
        }
        // Assign new role
        await api.post(`/users/${id}/roles`, {
          role_id: parseInt(data.roleId)
        });
      }
      
      // Handle department change if needed
      if (data.departmentId !== undefined) {
        const currentDepartmentId = user?.departments?.[0]?.id;
        const newDepartmentId = data.departmentId ? parseInt(data.departmentId) : null;
        
        console.log('Department change:', { currentDepartmentId, newDepartmentId, rawValue: data.departmentId });
        
        if (currentDepartmentId !== newDepartmentId) {
          // Remove from current department if exists
          if (currentDepartmentId) {
            console.log('Removing user from department:', currentDepartmentId);
            try {
              await api.delete(`/users/${id}/departments/${currentDepartmentId}`);
              console.log('Successfully removed from department');
            } catch (error) {
              console.warn('Department removal failed:', error);
              // Continue with the process even if removal fails
            }
          }
          // Add to new department if selected
          if (newDepartmentId) {
            console.log('Adding user to department:', newDepartmentId);
            try {
              await api.post(`/users/${id}/departments`, {
                department_id: newDepartmentId
              });
              console.log('Successfully added to department');
            } catch (error) {
              console.warn('Department assignment failed:', error);
              // Don't fail the whole operation
            }
          }
        }
      }
      
      // Handle team change if needed
      if (data.teamId !== undefined) {
        const currentTeamId = user?.teams?.[0]?.id;
        const newTeamId = data.teamId ? parseInt(data.teamId) : null;
        
        console.log('Team change:', { currentTeamId, newTeamId, rawValue: data.teamId });
        
        if (currentTeamId !== newTeamId) {
          // Remove from current team if exists
          if (currentTeamId) {
            console.log('Removing user from team:', currentTeamId);
            try {
              await api.delete(`/users/${id}/teams/${currentTeamId}`);
              console.log('Successfully removed from team');
            } catch (error) {
              console.warn('Team removal failed:', error);
              // Continue with the process even if removal fails
            }
          }
          // Add to new team if selected
          if (newTeamId) {
            console.log('Adding user to team:', newTeamId);
            try {
              await api.post(`/users/${id}/teams`, {
                team_id: newTeamId
              });
              console.log('Successfully added to team');
            } catch (error) {
              console.warn('Team assignment failed:', error);
              // Don't fail the whole operation
            }
          }
        }
      }
      
      // Handle manager change if needed
      if (data.managerId !== undefined) {
        const currentManagerId = user?.managers?.[0]?.id;
        const newManagerId = data.managerId ? parseInt(data.managerId) : null;
        
        console.log('Manager change:', { currentManagerId, newManagerId, rawValue: data.managerId });
        
        if (currentManagerId !== newManagerId) {
          // Remove from current manager if exists
          if (currentManagerId) {
            console.log('Removing user from manager:', currentManagerId);
            try {
              await api.delete(`/users/${id}/managers/${currentManagerId}`);
              console.log('Successfully removed from manager');
            } catch (error) {
              console.warn('Manager removal failed:', error);
              // Continue with the process even if removal fails
            }
          }
          // Add to new manager if selected
          if (newManagerId) {
            console.log('Adding user to manager:', newManagerId);
            try {
              await api.post(`/users/${id}/managers`, {
                manager_id: newManagerId
              });
              console.log('Successfully added to manager');
            } catch (error) {
              console.warn('Manager assignment failed:', error);
              // Don't fail the whole operation
            }
          }
        }
      }
      
      return response.data;
    },
    onSuccess: () => {
      navigate('/users');
    }
  });

  const onSubmit = (data: UserFormData) => {
    if (isEditing) {
      updateUser.mutate(data);
    } else {
      createUser.mutate(data);
    }
  };

  const mutation = isEditing ? updateUser : createUser;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edytuj użytkownika' : 'Dodaj nowego użytkownika'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing ? 'Zaktualizuj informacje o użytkowniku' : 'Utwórz nowego użytkownika systemu'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informacje o użytkowniku</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {mutation.isError && (
              <Alert variant="error">
                Nie udało się {isEditing ? 'zaktualizować' : 'utworzyć'} użytkownika. 
                {mutation.error?.response?.data?.error && (
                  <div className="mt-2 text-sm">
                    Szczegóły: {mutation.error.response.data.error}
                  </div>
                )}
              </Alert>
            )}
            
            {(rolesError || departmentsError || teamsError || managersError) && (
              <Alert variant="warning">
                Niektóre dane pomocnicze nie zostały załadowane. Sprawdź połączenie z serwerem.
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Imię"
                {...register('firstName', { required: 'Imię jest wymagane' })}
                error={errors.firstName?.message}
              />
              <Input
                label="Nazwisko"
                {...register('lastName', { required: 'Nazwisko jest wymagane' })}
                error={errors.lastName?.message}
              />
              <Input
                label="Email"
                type="email"
                {...register('email', { 
                  required: 'Email jest wymagany',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Nieprawidłowy adres email'
                  }
                })}
                error={errors.email?.message}
              />
              <Input
                label="ID pracownika"
                {...register('employeeId', { 
                  required: !isEditing ? 'ID pracownika jest wymagane' : false 
                })}
                error={errors.employeeId?.message}
              />
              {!isEditing && (
                <Input
                  label="Hasło"
                  type="password"
                  {...register('password', { 
                    required: !isEditing ? 'Hasło jest wymagane' : false,
                    minLength: {
                      value: 6,
                      message: 'Hasło musi mieć co najmniej 6 znaków'
                    }
                  })}
                  error={errors.password?.message}
                />
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rola
                </label>
                <select
                  {...register('roleId', { required: 'Rola jest wymagana' })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  disabled={!roles || roles.length === 0}
                >
                  <option value="">
                    {rolesError ? 'Błąd ładowania ról' : !roles ? 'Ładowanie ról...' : 'Wybierz rolę'}
                  </option>
                  {roles?.map((role: any) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.roleId && (
                  <p className="mt-1 text-sm text-red-600">{errors.roleId.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dział
                </label>
                <select
                  {...register('departmentId')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  disabled={departmentsError}
                >
                  <option value="">
                    {departmentsError ? 'Błąd ładowania działów' : 'Wybierz dział'}
                  </option>
                  {departments?.map((department: any) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zespół
                </label>
                <select
                  {...register('teamId')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Wybierz zespół</option>
                  {teams?.map((team: any) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Przełożony
                </label>
                <select
                  {...register('managerId')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Brak przełożonego</option>
                  {managers?.map((manager: any) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.first_name || manager.firstName} {manager.last_name || manager.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Numer telefonu"
                {...register('phoneNumber')}
              />
              <Input
                label="Stanowisko"
                {...register('jobTitle')}
              />
              <Input
                label="Inicjały"
                {...register('initials')}
                placeholder="np. JK"
                maxLength={10}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Aktywny użytkownik
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('invoiceAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Dostęp do faktur
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('invoiceOnlyAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Tylko dostęp do faktur (bez zarządzania urlopami)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                Anuluj
              </Button>
              <Button
                type="submit"
                isLoading={mutation.isPending}
              >
                {isEditing ? 'Zaktualizuj użytkownika' : 'Utwórz użytkownika'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};